/**
 * What may go in every JSON column the lab authoring screens edit, written down once.
 *
 * The lab schema puts its closed lists in columns and its open-ended detail in JSON, which is the
 * right split for a database and a bad one for a form: `action` alone accepts twenty-one keys, four
 * of them closed lists, two of them with no default and a warning at compile time if they are left
 * unset. Handed to an author as a textarea, that is a wall of braces and a guess, and the guess is
 * usually wrong in the direction nobody notices - a step that runs, reports success, and proves
 * nothing.
 *
 * So each column is described here rather than in a `helpText` string next to a textarea: what it
 * is for, every key it accepts, what happens if a key is left out, and which keys are a choice from
 * a list rather than free text. Two things read it. `LabSpecBuilder` turns a description into real
 * controls - selects, numbers, tick boxes - so the common case never involves typing JSON at all.
 * `LabHelpMenu` turns the same description into the reference beside the field, so the escape hatch
 * is documented too.
 *
 * One source, because the alternative is a form that offers a key the backend refuses and a help
 * text that documents a default that changed. Everything here is taken from
 * `toolbox_db/db/services/lab_items.py` (the compile) and its Go counterpart
 * `go/internal/labs/compile.go` (the runtime), which are the two things that actually read these
 * columns.
 *
 * Unknown keys are never dropped. An author may write something this file has not caught up with,
 * and a form that silently deletes it on save would be worse than one that cannot render it.
 */

import {
  LabOption,
  LAB_ACTION_TYPES,
  LAB_AUTHORITIES,
  LAB_BLAST_RADII,
  LAB_CRITERION_STATES,
  LAB_EVALUATORS,
  LAB_FACT_OPERATORS,
  LAB_HEALTH_STATES,
  LAB_INSERT_MODES,
  LAB_INTEGRITY_STATES,
  LAB_PRECONDITION_SCOPES,
  LAB_PROGRESS_METRICS,
} from './labService';

/**
 * How one key inside a JSON column is edited.
 *
 * `tristate` is not a nicety: `disruptive` is nullable on purpose, and unset means "nobody has
 * judged this", which excludes the step from fleet sweeps. A tick box cannot say that, and one
 * that starts unticked would be an author deciding fleet safety by omission.
 */
export type LabSpecFieldKind =
  | 'text'
  | 'multiline'
  | 'number'
  | 'boolean'
  | 'tristate'
  | 'option'
  | 'optionList'
  | 'keyvalue'
  | 'stringList'
  | 'scopes'
  | 'factAssertion'
  | 'healthAssertion'
  | 'json';

/** Where a `stringList` gets its suggestions. The list itself is free text; these are the hints. */
export type LabSpecSuggestionSource = 'deviceKeys' | 'criterionKeys' | 'optionKeys' | 'stepKeys';

export type LabSpecField = {
  key: string;
  label: string;
  kind: LabSpecFieldKind;
  /** What this key does, in a sentence. Shown under the control and in the help menu. */
  help: string;
  /** For `option` and `optionList`. */
  options?: LabOption[];
  suggestions?: LabSpecSuggestionSource;
  placeholder?: string;
  /** What happens when it is left alone. Every key that has a default states it here. */
  fallback?: string;
  /** Only rendered when this holds, so the form asks nothing that does not apply. */
  when?: (spec: Record<string, any>) => boolean;
  /** For a number that is a duration, so the control can say "seconds" without repeating it. */
  unit?: string;
};

export type LabSpecGroup = {
  title: string;
  hint?: string;
  fields: LabSpecField[];
  /** Groups that hold the keys almost nobody sets start closed. Never a required one. */
  collapsed?: boolean;
};

export type LabJsonSchema = {
  key: string;
  title: string;
  /** What this column is for and who reads it. The first thing the help menu shows. */
  summary: string;
  /** The guided fields. Absent for a column whose keys are the author's own to invent. */
  groups?: LabSpecGroup[];
  /**
   * Set for a bag of caller-chosen keys - options, metadata, a request payload. There is no list
   * of keys to offer, so the editor is rows of key and value rather than a set of controls.
   */
  freeform?: {
    keyLabel: string;
    valueLabel: string;
    help: string;
    /** Keys worth suggesting even though anything is allowed. */
    suggestions?: LabOption[];
    /**
     * A list from the surrounding form to suggest as well.
     *
     * For the keys that are only knowable at the time - the options the item being attached actually
     * declares, say. Merged with the static suggestions above rather than replacing them.
     */
    suggestionSource?: LabSpecSuggestionSource;
  };
  examples?: { label: string; hint?: string; value: Record<string, any> }[];
  /** Worth knowing, and not a field. Rendered as a list in the help menu. */
  notes?: string[];
};

/* ------------------------------------------------------------------------- *
 * The step specs
 *
 * `action` and `validation` on a configuration item both compile through
 * `_step_defaults`, so they accept the same keys and are described once. The
 * difference between them is which phase the step lands in and whether it is
 * allowed to only make a change - which is a property of the field, not of the
 * key set, so it lives in the summaries below.
 * ------------------------------------------------------------------------- */

/** The three keys without which the step is not really a step. */
const STEP_CORE_FIELDS: LabSpecField[] = [
  {
    key: 'action_type',
    label: 'What kind of work this is',
    kind: 'option',
    options: LAB_ACTION_TYPES,
    help: 'Decides how the agent carries it out, and therefore what the payload has to contain.',
    fallback:
      "Taken from the execution method where one is named, and a plain shell command where none is - which is rarely what was meant.",
  },
  {
    key: 'payload',
    label: 'Payload',
    kind: 'keyvalue',
    help: 'What to do: the command, the config body, the request. Merged over the method\'s own template, and this wins on any key it sets.',
  },
  {
    key: 'expect',
    label: 'What makes it a pass',
    kind: 'keyvalue',
    help: 'Evaluated by the agent, so the verdict travels with the result. An expected status, a regex, a JSON path.',
    fallback:
      'Without this the step only records that it ran. Nothing decides whether it worked, and the run reads as green.',
  },
];

/** The two columns that gate fleet fan-out, and have no default on purpose. */
const STEP_SAFETY_FIELDS: LabSpecField[] = [
  {
    key: 'disruptive',
    label: 'Does running this interrupt somebody mid-exercise?',
    kind: 'tristate',
    help: 'One of the two answers a fleet-wide sweep checks before it touches a live pod.',
    fallback:
      'Left unstated, the step is excluded from every fleet sweep. That is the safe failure, but a silent one.',
  },
  {
    key: 'blast_radius',
    label: 'How far the effect reaches',
    kind: 'option',
    options: LAB_BLAST_RADII,
    help: 'The other fleet answer. Shared infrastructure needs a reason before it goes near a live session.',
    fallback: 'Left unset, the step is excluded from fleet sweeps for the same reason.',
  },
];

const STEP_TIMING_FIELDS: LabSpecField[] = [
  {
    key: 'timeout_seconds',
    label: 'Timeout',
    kind: 'number',
    unit: 'seconds',
    help: 'How long the agent waits before giving up on this one attempt.',
    fallback: "The method's own default, or 120 seconds where the method states none.",
  },
  {
    key: 'retry_limit',
    label: 'Retries',
    kind: 'number',
    help: 'How many further attempts after the first. Only safe where the work is idempotent.',
    fallback: 'None. One attempt.',
  },
  {
    key: 'retry_backoff_seconds',
    label: 'Wait between retries',
    kind: 'number',
    unit: 'seconds',
    help: 'How long to leave it before trying again.',
    fallback: '15 seconds.',
    when: (spec) => !!spec.retry_limit,
  },
  {
    key: 'recurring_seconds',
    label: 'Repeat every',
    kind: 'number',
    unit: 'seconds',
    help: 'Turns a one-off check into a watch. For a validation that has to keep holding, not for work.',
    fallback: 'Runs once.',
  },
];

const STEP_BEHAVIOUR_FIELDS: LabSpecField[] = [
  {
    key: 'name',
    label: 'Step name',
    kind: 'text',
    help: 'What this step is called in the run.',
    fallback: "The item's own name.",
  },
  {
    key: 'description',
    label: 'Step description',
    kind: 'multiline',
    help: 'What the learner or the proctor sees against it.',
    fallback: "The item's own description.",
  },
  {
    key: 'optional',
    label: 'Optional',
    kind: 'boolean',
    help: 'A failure here does not fail the phase. For an observation rather than a requirement.',
    fallback: 'Required.',
  },
  {
    key: 'gated',
    label: 'Gated',
    kind: 'boolean',
    help: 'Held back until a trigger releases it. Nothing else opens a gated step, so a gated step with no trigger never runs.',
    fallback: 'Runs as soon as its dependencies are satisfied.',
  },
  {
    key: 'idempotent',
    label: 'Safe to run again',
    kind: 'boolean',
    help: 'Governs both retry and whether this may go out in bulk.',
    fallback: 'Assumed safe. Untick it if a second run would do damage.',
  },
  {
    key: 'exclusive',
    label: 'Must not run alongside another exclusive step on the same device',
    kind: 'boolean',
    help: 'Some platforms serialize deployments whether or not the caller does.',
    fallback: 'May run concurrently.',
  },
  {
    key: 'destructive',
    label: 'Destroys something',
    kind: 'boolean',
    help: 'Recorded on the step so a sweep and a proctor can both see it before it goes anywhere.',
    fallback: 'Not destructive.',
  },
  {
    key: 'sensitive_output',
    label: 'Output may contain secrets',
    kind: 'boolean',
    help: "Makes the agent's scrubber mandatory for this step's output.",
    fallback: "Output is stored as reported, subject to the agent's usual truncation.",
  },
  {
    key: 'insert_mode',
    label: 'When this may be added to a run',
    kind: 'option',
    options: LAB_INSERT_MODES,
    help: 'Whether this is part of the lab from the start or something injected into runs already in progress.',
    fallback: 'Part of the lab. Materialized when the run opens.',
  },
  {
    key: 'authority',
    label: 'Who is allowed to run it',
    kind: 'option',
    options: LAB_AUTHORITIES,
    help: 'Whether the agent does this on its own or a person has to.',
    fallback: 'The agent, automatically.',
  },
  {
    key: 'emits_facts',
    label: 'Facts this contributes',
    kind: 'keyvalue',
    help: 'Values pulled out of the output and put in the run\'s fact dictionary, for a later step or a fact-match criterion to assert against.',
    fallback: 'Nothing is extracted. A fact-match criterion has nothing to read.',
  },
  {
    key: 'requires',
    label: 'Preconditions',
    kind: 'scopes',
    help: 'Checked before the work is attempted, so a step that could not have worked reports why instead of failing obscurely.',
    fallback: 'Nothing is checked first.',
  },
];

const STEP_SPEC_GROUPS: LabSpecGroup[] = [
  { title: 'The work', fields: STEP_CORE_FIELDS },
  {
    title: 'Fleet safety',
    hint: 'Neither has a default. Until both are answered this step is excluded from fleet-wide sweeps - which is the safe failure, and a silent one.',
    fields: STEP_SAFETY_FIELDS,
  },
  {
    title: 'Timing and retries',
    hint: 'All optional. The defaults are usually right.',
    fields: STEP_TIMING_FIELDS,
    collapsed: true,
  },
  {
    title: 'Rarely needed',
    hint: 'Overrides and flags. Everything here has a default that suits most steps.',
    fields: STEP_BEHAVIOUR_FIELDS,
    collapsed: true,
  },
];

/* ------------------------------------------------------------------------- *
 * Health and completion
 * ------------------------------------------------------------------------- */

const HEALTH_FIELDS: LabSpecField[] = [
  {
    key: 'devices',
    label: 'Devices that have to stay healthy',
    kind: 'stringList',
    suggestions: 'deviceKeys',
    help: 'Device keys. Each one named here is marked monitored on the section, so it appears in health reports and fleet sweeps.',
    fallback: 'The device this item is attached to.',
  },
  {
    key: 'recurring_seconds',
    label: 'Probe every',
    kind: 'number',
    unit: 'seconds',
    help: 'How often the probe step runs. Only generated when the item names a health method - the built-in monitoring needs no step.',
    fallback: '300 seconds.',
  },
  {
    key: 'scored',
    label: 'Score this',
    kind: 'boolean',
    help: 'Adds a device-health criterion, so staying up is part of the result rather than only a report.',
    fallback: 'Monitored but not scored.',
  },
  {
    key: 'states',
    label: 'States that count as healthy',
    kind: 'optionList',
    options: LAB_HEALTH_STATES,
    help: 'What the criterion accepts. A stale report becomes unknown rather than staying at its last value.',
    fallback: 'Up, and nothing else.',
    when: (spec) => !!spec.scored || !!spec.criterion,
  },
  {
    key: 'all',
    label: 'Every named device, not just one',
    kind: 'boolean',
    help: 'Untick to accept any one of them being healthy - for a pair where either will do.',
    fallback: 'All of them.',
    when: (spec) => !!spec.scored || !!spec.criterion,
  },
  {
    key: 'weight',
    label: 'Weight',
    kind: 'number',
    help: 'How much this counts towards the score, against the other criteria.',
    fallback: '1.',
    when: (spec) => !!spec.scored || !!spec.criterion,
  },
  {
    key: 'required',
    label: 'Required to pass',
    kind: 'boolean',
    help: 'The run cannot pass without it, whatever the weighted score says.',
    fallback: 'Not required - it contributes to the score and no more.',
    when: (spec) => !!spec.scored || !!spec.criterion,
  },
  {
    key: 'hold_seconds',
    label: 'Has to hold for',
    kind: 'number',
    unit: 'seconds',
    help: 'How long the state must persist before it counts. This is what separates "has been down five minutes" from "was down once".',
    fallback: 'Counts the moment it is observed.',
    when: (spec) => !!spec.scored || !!spec.criterion,
  },
];

const COMPLETION_FIELDS: LabSpecField[] = [
  {
    key: 'scored',
    label: 'Score this item',
    kind: 'boolean',
    help: 'Untick and no criterion is generated at all: the item runs and counts for nothing.',
    fallback: 'Scored. An empty spec still gets a criterion, because an unscored item is usually an oversight.',
  },
  {
    key: 'evaluator',
    label: 'How completion is decided',
    kind: 'option',
    options: LAB_EVALUATORS,
    help: "Steps passed is scoped to this item's own generated steps. Manual and external are reported from outside and are never recomputed.",
    fallback: "Steps passed, over this item's own steps.",
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'name',
    label: 'Criterion name',
    kind: 'text',
    help: 'What appears on the score sheet.',
    fallback: "The item's own name.",
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'description',
    label: 'Criterion description',
    kind: 'multiline',
    help: 'What is being judged, for whoever reads the result.',
    fallback: 'Nothing.',
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'weight',
    label: 'Weight',
    kind: 'number',
    help: 'How much this counts against the other criteria in the section.',
    fallback: '1.',
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'required',
    label: 'Required to pass',
    kind: 'boolean',
    help: 'The run cannot pass without it, whatever the weighted score says.',
    fallback: 'Required. Untick it for a criterion that only contributes to the score.',
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'hold_seconds',
    label: 'Has to hold for',
    kind: 'number',
    unit: 'seconds',
    help: 'How long the condition must persist before the criterion is met.',
    fallback: 'Met the moment it is observed.',
    when: (spec) => spec.scored !== false,
  },
  {
    key: 'fact_assertion',
    label: 'The fact to assert',
    kind: 'factAssertion',
    help: 'A dotted path into the run facts, an operator, and the value to compare against.',
    when: (spec) => spec.scored !== false && spec.evaluator === 'fact_match',
  },
  {
    key: 'health_assertion',
    label: 'The health to assert',
    kind: 'healthAssertion',
    help: 'Which devices, in which states, and whether all of them or any.',
    when: (spec) => spec.scored !== false && spec.evaluator === 'device_health',
  },
];

/* ------------------------------------------------------------------------- *
 * Trigger conditions
 * ------------------------------------------------------------------------- */

const TRIGGER_ELAPSED_FIELDS: LabSpecField[] = [
  {
    key: 'seconds',
    label: 'Wait',
    kind: 'number',
    unit: 'seconds',
    help:
      'How long after the anchor the trigger comes due. The anchor is when the run opened, or when the dCloud session started for a session-elapsed trigger.',
    fallback: 'Zero - it comes due immediately, which is a run-start trigger written the long way.',
  },
];

const TRIGGER_PROGRESS_FIELDS: LabSpecField[] = [
  {
    key: 'metric',
    label: 'Metric',
    kind: 'option',
    options: LAB_PROGRESS_METRICS,
    help: 'Which number on the run is compared. Anything else is refused by the evaluator.',
    fallback: 'Progress percent.',
  },
  {
    key: 'at_least',
    label: 'Fires at',
    kind: 'number',
    help: 'The floor. The condition holds once the metric reaches it.',
    fallback: 'Nothing. Without this the condition never holds.',
  },
];

const TRIGGER_CRITERION_FIELDS: LabSpecField[] = [
  {
    key: 'criterion',
    label: 'Criterion',
    kind: 'text',
    suggestions: 'criterionKeys',
    help: 'The key of the criterion to watch, exactly as authored on the section.',
  },
  {
    key: 'state',
    label: 'State to wait for',
    kind: 'option',
    options: LAB_CRITERION_STATES,
    help: 'The state the criterion has to reach for this to fire.',
    fallback: 'Met.',
  },
];

const TRIGGER_DEVICE_FIELDS: LabSpecField[] = [
  {
    key: 'device_key',
    label: 'Device',
    kind: 'text',
    suggestions: 'deviceKeys',
    help: 'The device whose health is watched.',
  },
  {
    key: 'state',
    label: 'State to wait for',
    kind: 'option',
    options: LAB_HEALTH_STATES,
    help:
      'A stale report reads as unknown rather than staying at its last value, so a pod that stopped answering does not keep a "up" trigger disarmed.',
    fallback: 'Down.',
  },
];

/* ------------------------------------------------------------------------- *
 * The catalogue
 * ------------------------------------------------------------------------- */

export const LAB_JSON_SCHEMAS: Record<string, LabJsonSchema> = {
  /* ---- Configuration item ---------------------------------------------- */

  itemOptions: {
    key: 'itemOptions',
    title: 'Options',
    summary:
      "The knobs this item exposes, and their defaults. Anywhere in the four specs below, {{name}} or {{ name }} is substituted before the step is written - a token that is the whole value keeps its type, one inside a string is interpolated. A scenario attaching the item can override any of them, which is what makes one item serve two labs that differ only in a network or a policy name.",
    freeform: {
      keyLabel: 'Option',
      valueLabel: 'Default',
      help: 'Flat. A nested object is not reachable by a token, and an override of half of one is a value nobody can predict by reading either row.',
    },
    examples: [
      {
        label: 'A network and a policy name',
        value: { network: '10.0.0.0/24', policy_name: 'PseudoCo-Baseline' },
      },
    ],
    notes: [
      'An option a spec refers to and this does not set is left as written in the generated step, and the compile warns about it.',
      'Overrides are shallow, per key. Lists and objects are replaced whole rather than merged.',
    ],
  },

  itemAction: {
    key: 'itemAction',
    title: 'Action',
    summary:
      'The work, as the step it becomes. Lands in the setup phase, unless the item says pods arrive with it already applied - in which case no setup step is generated at all. Leave it empty for an item that only checks something.',
    groups: STEP_SPEC_GROUPS,
    examples: [
      {
        label: 'An API call',
        hint: 'Tokens come from the options above.',
        value: {
          action_type: 'api_call',
          payload: { path: '/policy', body: { name: '{{policy_name}}' } },
          expect: { status: 200 },
          disruptive: false,
          blast_radius: 'device',
        },
      },
      {
        label: 'A shell command',
        value: {
          action_type: 'command',
          payload: { command: 'systemctl enable --now suricata' },
          expect: { exit_code: 0 },
          disruptive: false,
          blast_radius: 'device',
        },
      },
    ],
  },

  itemValidation: {
    key: 'itemValidation',
    title: 'Validation',
    summary:
      'How you know the work landed. Lands in the validate phase, or in bootstrap when the item claims pods arrive with it already applied - which is what makes a false pre-built claim fail immediately rather than halfway through. The method used here has to be able to observe state: one that only applies reports its own exit code, and the lab then passes whether or not the pod is in the state claimed.',
    groups: STEP_SPEC_GROUPS,
    examples: [
      {
        label: 'Read it back',
        value: {
          action_type: 'api_call',
          payload: { path: '/policy/{{policy_name}}' },
          expect: { status: 200 },
          disruptive: false,
          blast_radius: 'device',
        },
      },
      {
        label: 'Keep checking',
        hint: 'Recurring, for a state that has to keep holding.',
        value: {
          action_type: 'http_check',
          payload: { url: 'https://{{address}}/api/health' },
          expect: { status: 200 },
          recurring_seconds: 60,
          disruptive: false,
          blast_radius: 'device',
        },
      },
    ],
  },

  itemHealthCheck: {
    key: 'itemHealthCheck',
    title: 'Health check',
    summary:
      'Which devices this item expects to stay healthy. Naming one marks it monitored on every section the item is attached to, so it appears in health reports and fleet sweeps. Asking for it to be scored adds a device-health criterion; naming a health method also generates a recurring probe step, for the questions built-in monitoring cannot answer - "is the tunnel still passing traffic" is not a port check.',
    groups: [
      { title: 'What is watched', fields: HEALTH_FIELDS.slice(0, 3) },
      {
        title: 'How it is scored',
        hint: 'Only read when this is scored.',
        fields: HEALTH_FIELDS.slice(3),
        collapsed: true,
      },
    ],
    examples: [
      {
        label: 'Watch two boxes and score them',
        value: { devices: ['fmc', 'ftd-1'], scored: true, hold_seconds: 120 },
      },
    ],
    notes: [
      '`device_keys` is accepted as a synonym for `devices`: the model documents one and this form suggests the other, and an author who used either meant the same thing.',
      'A device named here that the section does not have is reported by the compile and monitors nothing.',
    ],
  },

  itemCompletion: {
    key: 'itemCompletion',
    title: 'Completion criteria',
    summary:
      "What counts as having done it. Left empty, the item still gets a criterion - steps passed, over its own generated steps - because an item that runs and counts for nothing is almost always an oversight. Naming a completion method instead generates a step that goes and asks, for the cases where completion is something to be confirmed rather than inferred.",
    groups: [
      { title: 'Scoring', fields: COMPLETION_FIELDS.slice(0, 2) },
      {
        title: 'How it reads and what it weighs',
        fields: COMPLETION_FIELDS.slice(2),
        collapsed: true,
      },
    ],
    examples: [
      { label: 'Count it double, and require it', value: { weight: 2, required: true } },
      { label: 'Do not score this item at all', value: { scored: false } },
    ],
    notes: [
      'Steps passed is scoped to this item\'s own steps. Left unscoped it would mean every validation step in the section, and every item\'s criterion would then be met or unmet together.',
      'Manual and external verdicts come from outside and are never recomputed - re-deriving one destroys it.',
    ],
  },

  itemMetadata: {
    key: 'itemMetadata',
    title: 'Metadata',
    summary:
      'Anything this item carries that none of the specs cover. Nothing in the compile reads it, so it is a note to whoever comes next rather than a way to change behaviour. Empty is normal.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Free-form. Nothing validates it and nothing runs from it.',
      suggestions: [
        { value: 'owner', label: 'owner', hint: 'Who maintains this item.' },
        { value: 'source', label: 'source', hint: 'Where the procedure came from.' },
        { value: 'ticket', label: 'ticket', hint: 'The change this was written for.' },
      ],
    },
  },

  attachmentOptionOverrides: {
    key: 'attachmentOptionOverrides',
    title: 'Option values for this section',
    summary:
      "This section's answers to the item's options. Only the keys the item declares are worth setting - anything else is carried and never substituted. Shallow, per key: an override replaces a value whole rather than merging into it.",
    freeform: {
      keyLabel: 'Option',
      valueLabel: 'Value for this section',
      help: "Overrides the item's default for this attachment only. Every other section keeps the default.",
      // The item's own declared options, offered as buttons - an override of a key the item does not
      // declare is carried, never substituted, and reads exactly like one that works.
      suggestionSource: 'optionKeys',
    },
  },

  /* ---- Device ---------------------------------------------------------- */

  deviceAttributes: {
    key: 'deviceAttributes',
    title: 'Attributes',
    summary:
      "Facts about this box that a step's payload wants to template off - interface names, a policy name, a management VRF. Read by whatever refers to them and by nothing else.",
    freeform: {
      keyLabel: 'Attribute',
      valueLabel: 'Value',
      help: 'Never a credential. This row is sent to every agent that enrols, and a password here would be a disclosure with each enrollment.',
      suggestions: [
        { value: 'inside_interface', label: 'inside_interface' },
        { value: 'outside_interface', label: 'outside_interface' },
        { value: 'management_vrf', label: 'management_vrf' },
        { value: 'domain', label: 'domain' },
      ],
    },
    notes: [
      'No username, no password, no key. The device names a credential in its own field and the pod resolves that reference per session.',
    ],
  },

  /* ---- Execution method ------------------------------------------------ */

  methodPayloadTemplate: {
    key: 'methodPayloadTemplate',
    title: 'Payload template',
    summary:
      "What is true of every use of this method: the interpreter, the flags that make it non-interactive, the working directory. Merged *under* an item's own payload, so the item wins on any key it sets. Not what is being configured - that belongs on the item.",
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Merged shallowly under the item payload, key by key.',
    },
    examples: [
      {
        label: 'A non-interactive PowerShell',
        value: { shell: 'powershell', args: ['-NoProfile', '-NonInteractive'] },
      },
    ],
  },

  methodExpectTemplate: {
    key: 'methodExpectTemplate',
    title: 'Expect template',
    summary:
      "The floor for this method - what has to be true of any step carried out by it. Merged under the item's own expectation, which wins. The assertion for the actual work belongs on the item.",
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Merged shallowly under the item expectation, key by key.',
    },
    examples: [{ label: 'A clean exit', value: { exit_code: 0 } }],
  },

  methodMetadata: {
    key: 'methodMetadata',
    title: 'Metadata',
    summary: 'Anything this method carries that its own columns do not cover. Empty is normal.',
    freeform: { keyLabel: 'Key', valueLabel: 'Value', help: 'Free-form. Nothing reads it.' },
  },

  /* ---- Scenario step (authored directly, not compiled) ----------------- */

  stepPayload: {
    key: 'stepPayload',
    title: 'Payload',
    summary:
      'What the agent is asked to do. What belongs in here depends entirely on the kind of work above it: a command has a command, an API call has a path and a body, a wait has a duration.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Merged over the execution method\'s template where one is in play, and this wins.',
    },
    examples: [
      { label: 'A command', value: { command: 'show running-config | include crypto' } },
      { label: 'An API call', value: { method: 'POST', path: '/api/policy', body: { name: 'baseline' } } },
      { label: 'A port check', value: { host: '198.18.133.10', port: 443 } },
    ],
  },

  stepExpect: {
    key: 'stepExpect',
    title: 'What makes this a pass',
    summary:
      'Evaluated by the agent, so a step\'s verdict travels with its result rather than being decided later from output somebody has to parse.',
    freeform: {
      keyLabel: 'Assertion',
      valueLabel: 'Expected',
      help: 'Without any of these the step only records that it ran, and the run reads as green whatever happened.',
      suggestions: [
        { value: 'status', label: 'status', hint: 'An HTTP status code.' },
        { value: 'exit_code', label: 'exit_code', hint: 'Usually 0.' },
        { value: 'contains', label: 'contains', hint: 'A substring the output must have.' },
        { value: 'matches', label: 'matches', hint: 'A regular expression the output must match.' },
        { value: 'json_path', label: 'json_path', hint: 'A path into the response and the value it must hold.' },
      ],
    },
    examples: [
      { label: 'An HTTP success', value: { status: 200 } },
      { label: 'A clean command', value: { exit_code: 0, contains: 'Enabled' } },
    ],
  },

  stepEmitsFacts: {
    key: 'stepEmitsFacts',
    title: 'Facts this contributes',
    summary:
      "Values pulled out of this step's output and put into the run's fact dictionary, where a later step or a fact-match criterion can assert on them. This is how an exotic check gets done: compute it in a step, emit the answer, assert on the fact.",
    freeform: {
      keyLabel: 'Fact name',
      valueLabel: 'Where it comes from',
      help: 'The name is what a criterion asserts on by dotted path. Nested names make nested facts.',
    },
    examples: [{ label: 'Pull a tunnel state out', value: { tunnel_state: '$.result.state' } }],
  },

  stepRequires: {
    key: 'stepRequires',
    title: 'Preconditions',
    summary:
      'Checked before the work is attempted, so a step that could not possibly have worked says why rather than failing obscurely. Keyed by scope, and the loader refuses any scope not in the list - which is why this is a set of choices and not a free-form object.',
    groups: [
      {
        title: 'Scopes',
        fields: [
          {
            key: '',
            label: 'Preconditions',
            kind: 'scopes',
            help: 'Add the scopes this step depends on. Integrity takes a state; the rest are simply required.',
          },
        ],
      },
    ],
    examples: [
      { label: 'Needs to be reachable and authorised', value: { connectivity: true, access: true } },
      {
        label: 'Expects an already-compromised host',
        hint: 'For a threat-protection exercise that starts from a breach.',
        value: { integrity: 'compromised_expected' },
      },
    ],
    notes: [
      `The scopes an agent evaluates: ${LAB_PRECONDITION_SCOPES.map((scope) => scope.value).join(', ')}. Anything else is carried and ignored, and the compile says so.`,
    ],
  },

  /* ---- Success criterion ----------------------------------------------- */

  criterionFactAssertion: {
    key: 'criterionFactAssertion',
    title: 'Fact assertion',
    summary:
      'Matched against the facts steps emitted into the run. The operators are a fixed table rather than an expression language, so that a row in this table can never become code running in the evaluator.',
    groups: [
      {
        title: 'The assertion',
        fields: [
          {
            key: 'path',
            label: 'Fact path',
            kind: 'text',
            help: 'A dotted path into the run facts, e.g. tunnel.state. A fact that has not been collected yet leaves the criterion pending rather than failing it.',
          },
          {
            key: 'operator',
            label: 'Comparison',
            kind: 'option',
            options: LAB_FACT_OPERATORS,
            help: 'How the fact is compared to the value.',
            fallback: 'Truthy - the fact simply has to be set to something.',
          },
          {
            key: 'value',
            label: 'Value',
            kind: 'json',
            help: 'What to compare against. A string, a number, a list for "is one of". Not needed for truthy or exists.',
            when: (spec) => !['truthy', 'exists'].includes(String(spec.operator || 'truthy')),
          },
        ],
      },
    ],
    examples: [
      { label: 'A tunnel is up', value: { path: 'tunnel.state', operator: 'eq', value: 'up' } },
      { label: 'At least three hosts enrolled', value: { path: 'endpoints.enrolled', operator: 'gte', value: 3 } },
    ],
    notes: [
      'Comparing a string to a number is a scenario bug, not a learner failure, and it is reported as an error rather than as unmet.',
    ],
  },

  criterionHealthAssertion: {
    key: 'criterionHealthAssertion',
    title: 'Health assertion',
    summary:
      'Matched against the device health the agent reports for the session. Only read by a device-health criterion.',
    groups: [
      {
        title: 'The assertion',
        fields: [
          {
            key: 'device_keys',
            label: 'Devices',
            kind: 'stringList',
            suggestions: 'deviceKeys',
            help: 'Which devices are judged. Keys, as authored on the device.',
          },
          {
            key: 'states',
            label: 'States that count as healthy',
            kind: 'optionList',
            options: LAB_HEALTH_STATES,
            help: 'A stale report becomes unknown rather than staying at its last value, so a pod that stopped answering does not read as up.',
            fallback: 'Up, and nothing else.',
          },
          {
            key: 'all',
            label: 'Every device, not just one',
            kind: 'boolean',
            help: 'Untick to accept any one of them being healthy.',
            fallback: 'All of them.',
          },
        ],
      },
    ],
    examples: [
      { label: 'Both firewalls up', value: { device_keys: ['ftd-1', 'ftd-2'], states: ['up'], all: true } },
    ],
  },

  /* ---- Trigger --------------------------------------------------------- */

  /**
   * One schema per trigger type rather than one with every group in it.
   *
   * The condition is not one shape - `state` means a criterion state on a criterion trigger and a
   * health state on a device trigger, so a single form would have to offer two different lists under
   * one label and would show a blank select for whichever list the saved value did not come from.
   * Picked by `labTriggerConditionSchema`.
   */
  triggerConditionElapsed: {
    key: 'triggerConditionElapsed',
    title: 'Condition',
    summary:
      'When this comes due. Computed once, when the run opens, so changing it afterwards does not move a trigger that is already armed.',
    groups: [{ title: 'The wait', fields: TRIGGER_ELAPSED_FIELDS }],
    examples: [
      { label: 'Fifteen minutes in', value: { seconds: 900 } },
      { label: 'Half an hour in', value: { seconds: 1800 } },
    ],
  },

  triggerConditionProgress: {
    key: 'triggerConditionProgress',
    title: 'Condition',
    summary: 'Which number on the run has to reach what.',
    groups: [{ title: 'The threshold', fields: TRIGGER_PROGRESS_FIELDS }],
    examples: [
      { label: 'Half way through', value: { metric: 'progress_percent', at_least: 50 } },
      { label: 'Three criteria met', value: { metric: 'criteria_met', at_least: 3 } },
    ],
    notes: [
      'A metric the evaluator does not know is reported as an error rather than never firing quietly.',
    ],
  },

  triggerConditionCriterion: {
    key: 'triggerConditionCriterion',
    title: 'Condition',
    summary: 'Which criterion has to reach which state.',
    groups: [{ title: 'The criterion', fields: TRIGGER_CRITERION_FIELDS }],
    examples: [
      { label: 'Once the baseline is met', value: { criterion: 'baseline-policy@fmc', state: 'met' } },
      { label: 'When a check comes back unmet', value: { criterion: 'tunnel-up', state: 'unmet' } },
    ],
    notes: [
      'The key has to be a criterion on this section. A key that matches nothing never fires, and nothing reports that it was looking.',
      'Waived counts. A proctor setting a criterion aside fires a trigger waiting on met only if you asked for waived too.',
    ],
  },

  triggerConditionDevice: {
    key: 'triggerConditionDevice',
    title: 'Condition',
    summary:
      'Which device has to reach which health state. Read from the health the agent reports for the session, so a run with no session never fires this.',
    groups: [{ title: 'The device', fields: TRIGGER_DEVICE_FIELDS }],
    examples: [
      { label: 'When a firewall goes down', value: { device_key: 'ftd-1', state: 'down' } },
    ],
    notes: [
      'Hold seconds on the trigger is what separates "has been down for five minutes" from "was down once, five minutes ago".',
    ],
  },

  triggerActionDetail: {
    key: 'triggerActionDetail',
    title: 'Action detail',
    summary:
      'Extra arguments for whatever the trigger does. Emitting an event merges this into the event payload; opening an assist case reads the subject, detail, severity and device from it. The step-releasing actions read nothing here - they read the steps the trigger names.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'For an assist case: subject, detail, severity, device_key. For an event: anything the consumer expects.',
      suggestions: [
        { value: 'subject', label: 'subject', hint: 'The case title. Defaults to "Automatic: <trigger name>".' },
        { value: 'detail', label: 'detail', hint: 'The body of the case.' },
        { value: 'severity', label: 'severity', hint: 'low, normal, high or blocking. Defaults to normal.' },
        { value: 'device_key', label: 'device_key', hint: 'The device the case is about.' },
      ],
    },
    examples: [
      {
        label: 'Open a case about a firewall',
        value: { subject: 'Edge firewall is down', severity: 'high', device_key: 'ftd-1' },
      },
    ],
  },

  /* ---- Metadata on the rest of the tree -------------------------------- */

  labMetadata: {
    key: 'labMetadata',
    title: 'Metadata',
    summary:
      'What this lab carries that its own columns do not. Every lab in this toolbox is set at PseudoCo, the house fictional customer, and the initiative is what the lab is meant to demonstrate.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Free-form. Nothing in the compile reads it.',
      suggestions: [
        { value: 'customer', label: 'customer', hint: 'PseudoCo. The scenario loader enforces it.' },
        { value: 'initiative', label: 'initiative', hint: 'What this lab is meant to demonstrate.' },
      ],
    },
    examples: [{ label: 'The house customer', value: { customer: 'PseudoCo', initiative: 'Hybrid Mesh Firewall' } }],
  },

  scenarioMetadata: {
    key: 'scenarioMetadata',
    title: 'Metadata',
    summary:
      'What this section carries that its own columns do not. A section is reusable across labs, so anything here should be true of it everywhere - a fact about one lab belongs on that lab.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Free-form. Nothing in the compile reads it.',
      suggestions: [
        { value: 'customer', label: 'customer', hint: 'PseudoCo. The scenario loader enforces it.' },
        { value: 'initiative', label: 'initiative', hint: 'What this section is meant to demonstrate.' },
      ],
    },
  },

  configurationMetadata: {
    key: 'configurationMetadata',
    title: 'Metadata',
    summary:
      'What this publication needs that the lab does not carry - anything true of this lab in this data centre and nowhere else. Empty is normal.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Free-form. Nothing reads it.',
    },
  },

  deviceTypeMetadata: {
    key: 'deviceTypeMetadata',
    title: 'Metadata',
    summary:
      'What every device of this kind carries that the type\'s own columns do not. A fact about one box belongs in that device\'s attributes instead.',
    freeform: { keyLabel: 'Key', valueLabel: 'Value', help: 'Free-form. Nothing reads it.' },
  },

  eventMetadata: {
    key: 'eventMetadata',
    title: 'Metadata',
    summary:
      'What this occasion needs: the room, the cohort, the ticket it came from. Nothing runs from it.',
    freeform: {
      keyLabel: 'Key',
      valueLabel: 'Value',
      help: 'Free-form. Nothing reads it.',
      suggestions: [
        { value: 'room', label: 'room' },
        { value: 'cohort', label: 'cohort' },
        { value: 'ticket', label: 'ticket' },
      ],
    },
  },
};

/**
 * Which condition schema a trigger type reads, or null when it reads nothing.
 *
 * Run-start and manual triggers have no condition at all: a run-start trigger fires when the run
 * opens and a manual one fires when somebody fires it, so offering them a form would be offering
 * an author a field that is silently ignored.
 */
export const labTriggerConditionSchema = (
  triggerType?: string | null,
): LabJsonSchema | null => {
  switch (triggerType) {
    case 'elapsed':
    case 'session_elapsed':
      return LAB_JSON_SCHEMAS.triggerConditionElapsed;
    case 'progress':
      return LAB_JSON_SCHEMAS.triggerConditionProgress;
    case 'criterion_met':
      return LAB_JSON_SCHEMAS.triggerConditionCriterion;
    case 'device_state':
      return LAB_JSON_SCHEMAS.triggerConditionDevice;
    default:
      return null;
  }
};

/** Every key a schema documents, for the round-trip check that decides what is "unrecognised". */
export const labSchemaKeys = (schema: LabJsonSchema): string[] =>
  (schema.groups || []).flatMap((group) => group.fields.map((field) => field.key)).filter(Boolean);

/**
 * Whether a spec holds anything the builder cannot render.
 *
 * Used to force the JSON view open rather than to complain: an author who wrote a key this file has
 * not caught up with should still be able to see it, and it must survive the save either way.
 */
export const labSchemaExtraKeys = (
  schema: LabJsonSchema,
  value: Record<string, any> | null | undefined,
): string[] => {
  if (!value || schema.freeform) return [];

  // A field with no key of its own edits the whole object - the precondition scopes are the one
  // case - so there is nothing it could be failing to cover.
  const whole = (schema.groups || []).some((group) => group.fields.some((field) => !field.key));

  if (whole) return [];

  const known = new Set(labSchemaKeys(schema));

  // `devices` and `device_keys` are the same question, and only one of them is a field.
  if (known.has('devices')) known.add('device_keys');
  if (known.has('scored')) known.add('criterion');

  return Object.keys(value).filter((key) => !known.has(key));
};

/** The scopes a `requires` object may be keyed by, and how each one's value is edited. */
export const LAB_SCOPE_FIELDS = LAB_PRECONDITION_SCOPES.map((scope) => ({
  ...scope,
  /** Integrity is the only scope whose value is a choice rather than "this is required". */
  states: scope.value === 'integrity' ? LAB_INTEGRITY_STATES : null,
}));
